/**
 * VueI18n instance — imported by main.ts and the router.
 *
 * Locale resolution order (first truthy value wins):
 *   1. `lang` query-string parameter (e.g. ?lang=fr from the parent iframe)
 *   2. `rs_locale` in localStorage (persisted from a previous page load)
 *   3. Hard default: 'en'
 *
 * The resolved locale is written back to localStorage immediately so it
 * survives any in-app redirect (e.g. /login?redirect=/properties&lang=fr -> /properties).
 */

/* eslint-disable import/no-extraneous-dependencies */
import Vue from 'vue';
import VueI18n from 'vue-i18n';
import { messages } from '@/translations';
import { TLanguage } from '@/shared/types/language';

Vue.use(VueI18n);

const LOCALE_STORAGE_KEY = 'rs_locale';
const SUPPORTED_LOCALES: TLanguage[] = ['en', 'fr'];
const DEFAULT_LOCALE: TLanguage = 'en';
const isIFrame = window.self !== window.top;

/**
 * Resolves the active locale in priority order:
 * 1. `lang` query-string parameter: set by the parent iframe on every navigation.
 * 2. `rs_locale` in localStorage: persists the locale across redirects that strip query params.
 * 3. `DEFAULT_LOCALE`: hard fallback when neither source is available.
 *
 * French is ONLY allowed when the app is running inside an iframe.
 * In standalone mode the locale is always forced to `DEFAULT_LOCALE` (en).
 */
const resolveLocale = (): TLanguage => {
  if (!isIFrame) {
    return DEFAULT_LOCALE;
  }

  const params = new URLSearchParams(window.location.search);
  const queryLang = params.get('lang') as TLanguage | null;
  if (queryLang && SUPPORTED_LOCALES.includes(queryLang)) {
    return queryLang;
  }

  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY) as TLanguage | null;
    if (stored && SUPPORTED_LOCALES.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage unavailable (private browsing, etc.), fall through
  }

  return DEFAULT_LOCALE;
};

/**
 * Writes the given locale to localStorage under `LOCALE_STORAGE_KEY`.
 * Failures are silent, if persistence is unavailable the locale will still work for the current session,
 * it just won't survive the next redirect.
 */
const persistLocale = (locale: TLanguage): void => {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore write errors
  }
};

// Get locale and persist it immediately so that it survives any redirect that clears query params
const locale = resolveLocale();
persistLocale(locale);

const i18n = new VueI18n({
  locale,
  fallbackLocale: DEFAULT_LOCALE,
  messages,
  silentTranslationWarn: window?.APP_CONFIG?.VUE_APP_ENV === 'production',
});

export default i18n;
